
import React, { useState, useReducer, useEffect } from 'react';

import './Orders.list.scss'
import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';
import SectionTitleBar from '../components/titlebar';
import { useLazyQuery } from '@apollo/client';
import { graphqlSchema } from '../../services/graphql.schema';

import moment from 'moment';
import { AmazonOrderBinder } from '../../models/Orders';
import { FulfillmentStatuses, SearchOrder, SearchOrderBinded, SearchOrderPaginated } from '../../models/Orders_v2';
import { NavLink, Link } from 'react-router-dom';
import { Subject } from 'rxjs';
import { numberToCurrencyString } from '../../services/formatting';

import Pagination from '../Forms/Pagination';

import sharedToasterSubject from '../../services/shared.toasterSubject';
import { debounceTime } from 'rxjs/operators';
import InputCard from '../Forms/Input.cardForm';
import SelectCard from '../Forms/Select.cardForm';
import ToolTip from '../visuals/tooltip';
import Loading from '../Animations/loadScreen';

interface OrdersProps extends BaseReact {
    currentUser: ProfileQL;
}

moment.locale('es');

// const ENTRIES_PER_PAGE = 50;

function ordersReducer(state: { orders: SearchOrderBinded[] }, action: { type: string, payload: SearchOrderBinded[] }) {
    switch (action.type) {
        case 'set':
            console.log('<DISPATCH> action', action);
            return Object.assign({}, { orders: action.payload as SearchOrderBinded[] });
        case 'append':
            console.log('<DISPATCH> action', action);
            return Object.assign({}, { orders: state.orders.concat(JSON.parse(JSON.stringify(action.payload)) as SearchOrderBinded[]).sort((a, b) => new Date(b.order.createdTime).getTime() - new Date(a.order.createdTime).getTime()) });
        case 'update':
            return Object.assign({}, { orders: JSON.parse(JSON.stringify(action.payload)) as SearchOrderBinded[] });
        default:
            return Object.assign({}, { orders: JSON.parse(JSON.stringify(action.payload)) as SearchOrderBinded[] });
    }
}

function OrdersList({ history, location, currentUser }: OrdersProps) {
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);

    const [searchSubject] = useState(new Subject<string>());
    const [monthOverride, setMonthOverride] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [queryingByMonth, setQueryingByMonth] = useState(true);
    const [queryingByName, setQueryingByName] = useState(false);

    const [customFilter, setCustomFilter] = useState<'all' | 'billed' | 'un_billed'>('all');
    const [search, setSearch] = useState('');

    const [orders, dispatchOrders] = useReducer(ordersReducer, { orders: [] } as { orders: SearchOrderBinded[] });
    const [ordersSearch, dispatchOrdersSearch] = useReducer(ordersReducer, { orders: [] } as { orders: SearchOrderBinded[] });

    const [getOrdersQuery] = useLazyQuery(graphqlSchema.PROFILE.ORDERS.getOrders_v2, {
        onCompleted: ({ getOrders_v2 }: { getOrders_v2: SearchOrderPaginated }) => {
            console.log('Get Orders Query: ', getOrders_v2);
            dispatchOrders({ type: 'set', payload: getOrders_v2.orders });
            setPages(getOrders_v2.pages)
            setIsLoading(false);
            setQueryingByMonth(false);
        },
        onError: (e) => {
            setIsLoading(false);
            setQueryingByMonth(false);
            if (e.graphQLErrors[0]?.message) {
                console.error('E graphQLErrors: ', e.graphQLErrors[0].message);
                sharedToasterSubject.next({ type: 'error', message: e.graphQLErrors[0].message });
            }
        },
        fetchPolicy: 'cache-and-network'
    })
    const [getOrdersSearchQuery] = useLazyQuery(graphqlSchema.PROFILE.ORDERS.getOrdersSearch_v2, {
        onCompleted: ({ getOrdersSearch_v2 }: { getOrdersSearch_v2: SearchOrderBinded[] }) => {
            setIsLoading(false);
            setQueryingByName(false)
            console.log('Get Orders Search Query: ', getOrdersSearch_v2);
            dispatchOrdersSearch({ type: 'set', payload: getOrdersSearch_v2 });
        },
        onError: (e) => {
            setIsLoading(false);
            setQueryingByName(false)
            if (e.graphQLErrors[0]?.message) {
                console.error('E graphQLErrors: ', e.graphQLErrors[0].message);
                sharedToasterSubject.next({ type: 'error', message: e.graphQLErrors[0].message });
            }
        },
        fetchPolicy: 'cache-and-network'
    })

    useEffect(() => {
        const subs = searchSubject.pipe(debounceTime(850)).subscribe((searchValue) => {
            if (!!searchValue) {
                setIsLoading(true);
                setQueryingByName(true)
                getOrdersSearchQuery({
                    variables: {
                        search: searchValue
                    }
                });
            }
        })

        return () => {
            subs.unsubscribe()
        }

    }, [searchSubject, getOrdersSearchQuery])

    useEffect(() => {
        // isLoading is already set here
        getOrdersQuery({
            variables: {
                start: moment().startOf('month').toDate().getTime(),
                end: moment().endOf('hour').toDate().getTime(),
                page: 1
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSearch = (value: string) => {
        // Filter every char except number and dash
        const _v = value.split('').filter((char) => char.match(/[0-9,-]/g)).join('');
        setSearch(_v);
        searchSubject.next(_v);
    }

    const orderFilterOptions = [
        { value: 'all', label: 'Todos' },
        { value: 'billed', label: 'Facturados' },
        { value: 'un_billed', label: 'No Facturados' },
    ]

    const dayOverrideOptions = new Array(13).fill(0).map((_, i) => ({ value: i, label: moment().subtract(i, 'month').format(`MMMM, YYYY`) }))

    const changeMonthOverride = (monthOverride: 0 | 1 | 2) => {
        setMonthOverride(monthOverride);
        setIsLoading(true);
        setQueryingByMonth(true);
        // Changing month delete current orders and search for them
        dispatchOrders({ type: 'set', payload: [] });
        setPage(1);
        if (!monthOverride) {
            getOrdersQuery({
                variables: {
                    start: moment().startOf('month').toDate().getTime(),
                    end: moment().endOf('hour').toDate().getTime(),
                    page: 1
                }
            })
        } else {
            getOrdersQuery({
                variables: {
                    start: moment().subtract(monthOverride, 'month').startOf('month').toDate().getTime(),
                    end: moment().subtract(monthOverride, 'month').endOf('month').toDate().getTime(),
                    page: 1
                }
            })
        }
    }

    const changePage = (_page: number) => {
        if (isLoading) {
            // avoid multiclicking page
            return;
        }
        console.log('Changing page to: ', _page);
        setIsLoading(true);
        setPage(_page);
        if (!monthOverride) {
            getOrdersQuery({
                variables: {
                    start: moment().startOf('month').toDate().getTime(),
                    end: moment().endOf('hour').toDate().getTime(),
                    page: _page
                }
            })
        } else {
            getOrdersQuery({
                variables: {
                    start: moment().subtract(monthOverride, 'month').startOf('month').toDate().getTime(),
                    end: moment().subtract(monthOverride, 'month').endOf('month').toDate().getTime(),
                    page: _page
                }
            })
        }
    }


    const renderNoOrdersYet = () => {
        if (!isLoading && !!orders && !orders.orders.length) {
            return (
                <div className='_isEmpty'>
                    <h4>
                        ¡No encontramos pedidos a mostrar!
                    </h4>
                    <p>
                        Si recién conectaste tu cuenta, deben pasar algunas horas antes de cargar tus pedidos por primera vez.
                    </p>
                </div>
            )
        } else {
            return null;
        }
    }

    const ordersFiltered = orders.orders
        .filter((binded) => {
            if (customFilter === 'all') {
                return true
            }
            if (customFilter === 'billed') {
                return !!binded.binder
            } else {
                return !binded.binder
            }
        })


    const ordersPaginated = !!search ? ordersSearch.orders : ordersFiltered

    const statusLabel = (order: SearchOrder, binder: AmazonOrderBinder) => {
        if (order.orderStatus === 'CANCELLED') {
            return (
                <div className="_status pill red">
                    <p className=''>
                        Cancelada
                    </p>
                </div>
            )
        }
        if (!!binder && binder.billUUID) {
            return (
                <div className="_status pill green">
                    <p className=''>
                        Facturada
                    </p>
                </div>
            )
        }

        return (
            <div className="_status pill orange">
                <p className=''>
                    Sin Factura
                </p>
            </div>
        )
    }

    const renderShipppingLabel = (label: FulfillmentStatuses | undefined) => {
        if (label === 'PENDING') {
            return <p className="_shipping _pending">Pendiente</p>
        }
        else if (label === 'UNSHIPPED') {
            return <p className="_shipping _shipped">Envio listo</p>
        }
        else if (label === 'PARTIALLY_SHIPPED') {
            return <p className="_shipping _ready">Envio parcial</p>
        }
        else if (label === 'SHIPPED') {
            // return <p className="_shipping _shipped">Enviado</p>
            return <p className="_shipping _delivered">Enviado</p>
        } else {
            return <p className="_shipping">  </p>
        }
    }

    const renderLinkSent = (order: SearchOrder) => {
        if (!order._linkSent) {
            if (order.orderStatus === 'PENDING') {
                return (
                    <div className="_linkSent">
                        <p>
                            Pendiente
                        </p>
                        <ToolTip text={`El pedido debe iniciar envío para poder entregar link`} className="">
                            <p className="_pending">
                                Esperando Envío
                            </p>
                        </ToolTip>
                    </div>
                )
            }
            if (order.orderStatus === 'UNSHIPPED') {
                return (
                    <div className="_linkSent">
                        <p>
                            Pendiente
                        </p>
                        <ToolTip text={`El pedido debe iniciar envío para poder entregar link`} className="">
                            <p className="_pending">
                                Esperando Envío
                            </p>
                        </ToolTip>
                    </div>
                )
            }
            return (
                <div className="_linkSent"></div>
            )
        } else {
            return (
                <div className="_linkSent">
                    <p>
                        {moment(order._linkSent).fromNow()}
                    </p>
                    <p className="_success">
                        Enviado
                    </p>
                </div>
            )
        }
    }

    return (
        <div id="OrdersList">
            <SectionTitleBar currentUser={currentUser} title="Mis Pedidos" />
            <div className="ordersContent">
                <div className='card'>
                    <div className="toGlobal show-md">
                        <NavLink to="/ordenes/report" className='reportLink'>
                            <span>
                                Reporte de Pedidos
                            </span>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                                <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                            </svg>
                        </NavLink>
                        <NavLink to="/ordenes/globalstamp" className='globalLink'>
                            <span>
                                Factura Global
                            </span>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                                <path d="M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z" />
                            </svg>
                        </NavLink>
                    </div>
                    <div className='searchBlock'>
                        <InputCard
                            type="text"
                            value={search}
                            onChange={handleSearch}
                            label={`Filtrar por ID`}
                            icon='search'
                            placeholder="Filtrar tus pedidos por ID ayuda a buscar en la lista"
                        />
                    </div>

                    <div className="filterChange">
                        <div className='selects'>
                            <SelectCard value={customFilter} onChange={setCustomFilter} label={'Facturación'} options={orderFilterOptions} />
                            <SelectCard value={monthOverride} onChange={changeMonthOverride} label={'Por Mes'} options={dayOverrideOptions} disabled={isLoading} />
                        </div>

                        <div className="toGlobal">
                            <NavLink to="/ordenes/report" className='reportLink'>
                                <span>
                                    Crear reporte de Pedidos
                                </span>
                                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                                    <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                                </svg>
                            </NavLink>
                            <NavLink to="/ordenes/globalstamp">
                                <span>
                                    Ir a Facturas Globales
                                </span>
                                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                                    <path d="M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z" />
                                </svg>
                            </NavLink>
                        </div>
                    </div>

                    <div className="ordersList">
                        <div className="order header">
                            <p className="_id">
                                ID
                            </p>
                            <p className='_status'>
                                Estado
                            </p>
                            <p className="_title">
                                Fecha / Envío
                            </p>
                            <p className="_linkSent">
                                Link enviado
                            </p>
                            <p className="_uuid">
                                UUID Factura / UUID Reembolso
                            </p>
                            <p className="_price">
                                Precio total
                            </p>
                        </div>
                        {
                            queryingByMonth && !queryingByName ?
                                <div className="nameSearchMessage">
                                    <Loading relativePos={true} svgHeight={'30px'} svgWidth={'30px'} display={true} />
                                    <p>
                                        Espere un momento, estamos cargando los pedidos del mes
                                    </p>
                                </div>
                                : ''
                        }
                        {
                            queryingByName && !queryingByMonth ?
                                <div className="monthSearchMessage">
                                    <Loading relativePos={true} svgHeight={'30px'} svgWidth={'30px'} display={true} />
                                    <p>
                                        Espere un momento, estamos buscando pedidos con el ID
                                    </p>
                                </div>
                                : ''
                        }
                        {
                            isLoading && !queryingByName && !queryingByMonth ?
                                // true ?
                                <div className="nameSearchMessage">
                                    <Loading relativePos={true} svgHeight={'30px'} svgWidth={'30px'} display={true} />
                                    <p>
                                        Espere un momento, estamos cargando pedidos
                                    </p>
                                </div>
                                : ''
                        }
                        {
                            ordersPaginated
                                .map((orderbinded, i) => {
                                    const order = orderbinded.order;
                                    const binder = orderbinded.binder;
                                    return (
                                        <div className="order" key={order.orderId}>
                                            {
                                                !!order.proceeds?.grandTotal?.amount ?
                                                    <Link to={`/ordenes/stamp/${order.orderId}`}>
                                                        <div className='_id'>
                                                            <p className="_id">
                                                                {order.orderId}
                                                            </p>
                                                        </div>
                                                    </Link>
                                                    :
                                                    <div className='_id'>
                                                        <p className="_id">
                                                            {order.orderId}
                                                        </p>
                                                    </div>
                                            }

                                            {statusLabel(order, binder)}
                                            <div className="_title">
                                                <p>
                                                    {moment(order.createdTime).format('ddd MMM D, YYYY')}
                                                </p>
                                                {renderShipppingLabel(order.orderStatus)}

                                            </div>

                                            {renderLinkSent(order)}
                                            <div className="_uuid">
                                                <p className="_uuid">
                                                    {binder ? binder.billUUID : '--'} {!!binder && binder.billIsGlobal ? <b>(Global)</b> : ''}
                                                </p>
                                                <p className="_uuidRefund">
                                                    {binder && binder.refundBillUUID && binder.refundBillUUID.length ? binder.refundBillUUID[0].uuid : '--'}
                                                </p>
                                            </div>
                                            <div className='_price'>
                                                {
                                                    order.proceeds?.grandTotal ?
                                                        <p className="_price">
                                                            $ {numberToCurrencyString(order.proceeds.grandTotal.amount)} {order.proceeds.grandTotal.currencyCode}
                                                        </p>
                                                        : <p className="_price">--</p>
                                                }
                                                <div className='_statusSm'>
                                                    {statusLabel(order, binder)}
                                                </div >
                                            </div>
                                            {
                                                !order.proceeds?.grandTotal && !binder ?
                                                    <button className="svg noBill" disabled={true}>
                                                        <svg className={`${!!binder ? 'billed' : ''}`} xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                                                            <path d="M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z" />
                                                        </svg>
                                                    </button>
                                                    :
                                                    <Link className="svg" to={`/ordenes/stamp/${order.orderId}`}>
                                                        <svg className={`${!!binder ? (order.orderStatus === 'CANCELLED' ? 'isRefund' : 'billed') : ''}`} xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                                                            <path d="M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z" />
                                                        </svg>
                                                    </Link>
                                            }
                                        </div>
                                    )
                                })
                        }
                        {renderNoOrdersYet()}
                    </div>
                    <Pagination onPage={changePage} isDisabled={isLoading || !!search} pages={!!search ? 1 : pages} page={!!search ? 1 : page} />
                </div>
            </div>
        </div>
    )
}

export default OrdersList;